import { campaignPeriod, emptyMetrics, inPeriodMetrics, parseEnrollmentDate, periodCurve } from './enrollmentPeriod';
import { classroomKey, classroomName, gradeName, levelName, normalizeEnrollments, shiftName, textValue } from './enrollmentStatistics';
export const historyYears = endYear => Array.from({
  length: 5
}, (_, index) => Number(endYear) - 4 + index);
export const variation = (current, previous) => current === null || previous === null ? {
  absolute: null,
  percent: null
} : {
  absolute: current - previous,
  percent: previous > 0 ? (current - previous) / previous * 100 : null
};

// Guardar apenas agregados em memória: nenhum ID ou nome de aluno, nem ficha completa.
export function summarizeHistoryYear(rows, year, catalog) {
  const official = new Map();
  const catalogById = new Map();
  let nameYearMismatch = 0;
  catalog.forEach(row => {
    const id = textValue(row.TurmaID),
      name = textValue(row.Nome);
    const nameYear = /\b(20\d{2})\s*$/.exec(name)?.[1];
    const officialYear = textValue(row.AnoLetivo) || nameYear;
    if (!/^[1-9]\d*$/.test(id) || !name) return;
    catalogById.set(id, {
      name,
      year: officialYear || year,
      shift: shiftName(textValue(row.Turno))
    });
    if (/integral/i.test(name) || (officialYear && Number(officialYear) !== year)) return;
    if (nameYear && Number(nameYear) !== year) nameYearMismatch++;
    official.set(id, name);
  });
  const enriched = rows.map(row => {
    const classroom = catalogById.get(textValue(row.TurmaID));
    return classroom ? {
      ...row,
      NomeTurma: classroom.name,
      AnoLetivo: String(classroom.year)
    } : row;
  });
  const valid = normalizeEnrollments(enriched, year);
  const classrooms = new Map();
  const makeClassroom = (id, name, verified) => ({
    id,
    name,
    grade: gradeName(name),
    level: levelName(name),
    shift: catalogById.get(id)?.shift && catalogById.get(id).shift !== 'Não informado' ? catalogById.get(id).shift : shiftName(name),
    verified,
    total: 0,
    new: 0,
    returning: 0,
    unknown: 0,
    daily: {},
    undated: 0
  });
  official.forEach((name, id) => classrooms.set(id, makeClassroom(id, name, true)));
  let unmatched = 0;
  valid.forEach(row => {
    const id = textValue(row.TurmaID) || `sem-id:${classroomKey(classroomName(row))}`;
    if (!classrooms.has(id)) classrooms.set(id, makeClassroom(id, classroomName(row), false));
    const item = classrooms.get(id);
    if (!item.verified) unmatched++;
    item.total++;
    const type = Number(textValue(row.TipoContratoID));
    const metric = type === -1 ? 'new' : type === -2 ? 'returning' : 'unknown';
    item[metric]++;
    const date = parseEnrollmentDate(row.DataMatricula);
    if (date) {
      const bucket = item.daily[date] ||= emptyMetrics();
      bucket.total++;
      bucket[metric]++;
    } else item.undated++;
  });
  const names = new Map();
  const identity = row => `${classroomKey(row.name)} | ${row.shift}`;
  classrooms.forEach(row => names.set(identity(row), (names.get(identity(row)) || 0) + 1));
  let ambiguous = 0;
  const aggregated = [...classrooms.values()].map(row => {
    const name = identity(row);
    const collision = names.get(name) > 1;
    if (collision) ambiguous++;
    // IDs mudam entre anos. Comparar nomes só quando a correspondência for inequívoca.
    const key = row.verified && !collision ? name : `${name} [${year} · cadastro ${row.id}]`;
    return {
      ...row,
      key,
      baseKey: name,
      comparable: row.verified && !collision,
      sourceYear: year,
      name: row.verified && !collision ? row.name : key
    };
  });
  return {
    year,
    classrooms: aggregated,
    unmatched,
    ambiguous,
    nameYearMismatch,
    dateAggregation: true
  };
}
const normalizeSearch = value => value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[ºª°]/g, '').toLowerCase();
export function auditEnrollmentHistory(analysis, snapshots) {
  const checks = {
    annual: analysis.annual.every(row => !row.available || row.total === row.new + row.returning + row.unknown),
    classes: analysis.annual.every(row => !row.available || row.classrooms.reduce((sum, item) => sum + item.total, 0) === row.total),
    segments: analysis.annual.every(row => !row.available || analysis.segments.reduce((sum, item) => sum + (item.values[row.year]?.total || 0), 0) === row.total),
    buckets: Object.values(snapshots).every(snapshot => snapshot.error || !snapshot.dateAggregation || snapshot.classrooms.every(row => {
      const dates = Object.values(row.daily || {});
      return dates.reduce((sum, item) => sum + item.total, 0) + row.undated === row.total && dates.every(item => item.total === item.new + item.returning + item.unknown);
    })),
    curve: !analysis.period || analysis.annual.every(row => !row.available || analysis.curve.at(-1)?.[row.year] === row.total),
  };
  return { valid: Object.values(checks).every(Boolean), checks };
}

export function buildHistoryAnalysis(snapshots, endYear, {
  segment = '',
  search = '',
  period = null
} = {}) {
  const years = historyYears(endYear);
  const terms = normalizeSearch(search).trim().split(/\s+/).filter(Boolean);
  const annual = years.map(year => {
    const snapshot = snapshots[year];
    const range = campaignPeriod(period, endYear, year);
    if (!snapshot || snapshot.error || (period && !snapshot.dateAggregation)) return {
      year,
      available: false,
      total: null,
      new: null,
      returning: null,
      unknown: null,
      classrooms: [],
      range,
      excludedUndated: null
    };
    const matching = snapshot.classrooms.filter(row => (!segment || row.level === segment) && terms.every(term => normalizeSearch(row.key).includes(term)));
    const classrooms = period ? matching.map(row => ({
      ...row,
      ...inPeriodMetrics(row, range)
    })) : matching;
    const excludedUndated = period ? matching.reduce((sum, row) => sum + row.undated, 0) : 0;
    const totals = classrooms.reduce((result, row) => {
      for (const key of ['total', 'new', 'returning', 'unknown']) result[key] += row[key];
      return result;
    }, {
      total: 0,
      new: 0,
      returning: 0,
      unknown: 0
    });
    return {
      year,
      available: true,
      ...totals,
      classrooms,
      range,
      excludedUndated
    };
  }).map((row, index, rows) => ({
    ...row,
    ...variation(row.total, index ? rows[index - 1].total : null),
    newShare: row.total > 0 ? row.new / row.total * 100 : null
  }));
  const matrix = getIdentity => {
    const groups = new Map();
    annual.filter(row => row.available).forEach(({
      year,
      classrooms
    }) => classrooms.forEach(row => {
      const {
        key,
        name,
        grade,
        level,
        onlyYear
      } = getIdentity(row);
      if (!groups.has(key)) groups.set(key, {
        key,
        name,
        grade,
        level,
        onlyYear,
        values: {}
      });
      const item = groups.get(key);
      const totals = item.values[year] ||= {
        total: 0,
        new: 0,
        returning: 0,
        unknown: 0
      };
      for (const metric of ['total', 'new', 'returning', 'unknown']) totals[metric] += row[metric];
    }));
    return [...groups.values()].map(item => ({
      ...item,
      values: Object.fromEntries(years.map(year => [year, !annual.find(row => row.year === year).available || (item.onlyYear && item.onlyYear !== year) || snapshots[year]?.classrooms.some(row => !row.comparable && row.baseKey === item.key) ? null : item.values[year] || {
        total: 0,
        new: 0,
        returning: 0,
        unknown: 0
      }]))
    }));
  };
  const result = {
    years,
    annual,
    period,
    curve: period ? periodCurve(annual) : [],
    comparisons: annual.slice(0, -1).map(row => ({
      year: row.year,
      baseline: row.total,
      selected: annual[4].total,
      ...variation(annual[4].total, row.total),
      newDifference: variation(annual[4].new, row.new).absolute,
      returningDifference: variation(annual[4].returning, row.returning).absolute
    })),
    segments: matrix(row => ({
      key: row.level,
      name: row.level,
      level: row.level
    })),
    classrooms: matrix(row => ({
      key: row.key,
      name: row.comparable ? `${classroomKey(row.name)}${shiftName(row.name) !== row.shift ? ` · turno cadastrado: ${row.shift}` : ''}` : row.key,
      grade: row.grade,
      level: row.level,
      onlyYear: row.comparable ? undefined : row.sourceYear
    }))
  };
  result.audit = auditEnrollmentHistory(result, snapshots);
  return result;
}
